export interface RolePermissions {
  roleName: string;
  showBanner: boolean;
  lockTdo: boolean;
  isStatusLockEdits: boolean;
  // True when view-only was forced by object-level permissions (the caller lacks
  // AIWARE_TDO_UPDATE on this file) rather than by a lock, a URL mode, or a
  // redaction status. Only the banner copy varies on it — the permission set is
  // plain viewOnlyPermissions — because "ask your administrator" is the only
  // actionable advice for a denial the user cannot clear themselves.
  isOlpViewOnly: boolean;

  // Folder Manager
  uploadMedia: boolean;
  createRequest: boolean;
  addFolder: boolean;
  deleteMedia: boolean;
  moveMedia: boolean;
  openMenuDetails: boolean;
  // TODO could add ability to create/edit available redaction codes - not really import right now

  // File Viewer
  unLinkDeleteActions: boolean;
  generateVoiceMask: boolean;
  detectObjects: boolean;
  showDetectObjects: boolean;
  editDetections: boolean;

  // Media Details Top Bar
  editGlobalSettings: boolean;
  notifications: boolean;

  // Redacted File tab
  rightClickSaveRedactedMedia: boolean;
  downloadButtons: boolean;
  sendRedactedFile: boolean;
  deleteRedactedFile: boolean;

  // video editor - Cluster list
  toggleRedactions: boolean;
  selectClustersForMerge: boolean;
  addDetections: boolean;
  addBulkEffect: boolean;
  addFillEffects: boolean;
  clusterListFilter: boolean;
  removeFromOutput: boolean;
  editTdoName: boolean;
  editDetectionOrUDRsName: boolean;
  openClusterMenu: boolean;
  openOverlayMenu: boolean;
  dragOverlayObject: boolean;
  resizingOverlayObject: boolean;
  changeStatus: boolean;
  openSegmentMenu: boolean;
  customFill: boolean;
  showUndoRedo: boolean;
  editTimeLine: boolean;
  historySettings: boolean;
  rotateVideo: boolean;
  addUDRs: boolean;
  clickOnBoundingBox: boolean;
  pinCluster: boolean;
  transcribeAudio: boolean;
  timelineResizeObjectType: boolean;

  // video editor - Audio Tab
  audioMenuDetails: boolean;

  // video editor - Comment Tab
  addNewComment: boolean;
  viewHiddenComments: boolean;
  viewFilter: boolean;
  sortComment: boolean;
  export: boolean;
  commentMenuDetails: boolean;

  redactFile: boolean;
  editTrimRange: boolean;
  saveEditFiles: boolean;

  /**
   * May clear a file's status history (VE-26730).
   *
   * Its own flag rather than riding on `changeStatus`: clearing is not a status
   * change, it hides the record OF the changes, and the two want different
   * answers. Denied to view-only — a user who cannot alter a file must not be
   * able to hide what was done to it.
   *
   * Deliberately NOT withdrawn by the status edit lock. `statusLockChangePermissions`
   * inherits this `true` from review mode, because a locked status says the
   * file's redactions are frozen, not that its provenance may no longer be
   * administered.
   */
  clearStatusHistory: boolean;

  /**
   * May restructure the request's MERGED DOCUMENT (VE-31538) — add or remove
   * members, reorder them, rebuild it, rename it, or change its set-wide
   * Bates and watermark settings.
   *
   * Its own flag rather than riding on `saveEditFiles`: these act on the
   * whole set, not on the file on screen, and the set is also what the
   * project lock protects. The controls read it to DISABLE themselves,
   * rather than failing on click.
   *
   * THE VALUE ON THESE CONSTANTS IS NOT THE ANSWER. `selectRolePermissions`
   * overwrites it on whatever band it resolved, because this is a fact
   * about the SET and the bands are about the open FILE — a status lock
   * freezes that file's redactions and leaves the release's composition
   * alone, while a session parked on a file outside the set holds no lock
   * on the set at all and must not rewrite it, however editable its own
   * file is. Read `selectRolePermissions` for the real rule; what is here
   * is only the value a band would carry if nothing overwrote it.
   *
   * It is true for exactly one standing: this session holds the request's
   * merged document (or the request has none), it has object-level
   * permission on the open file, and the URL mode is neither viewOnly nor
   * review — recomposing the release is an editor's act.
   */
  editMergedDocument: boolean;

  // Keyboard shortcuts
  keyboardEditShortcuts: boolean;

  // Image mode specific permissions
  showAudioTab: boolean;
  showStickToVideo: boolean;
  showSetTimeStamp: boolean;
  showTrackOption: boolean;
  showTrimOptions: boolean;
  showDownloadRedactionReport: boolean;
}

export const editorPermissions: RolePermissions = {
  roleName: 'editor',
  showBanner: false,
  lockTdo: true,
  isStatusLockEdits: false,
  isOlpViewOnly: false,

  uploadMedia: true,
  createRequest: true,
  addFolder: true,
  deleteMedia: true,
  moveMedia: true,
  openMenuDetails: true,

  unLinkDeleteActions: true,
  detectObjects: true,
  showDetectObjects: true,
  generateVoiceMask: true,
  transcribeAudio: true,
  editDetections: true,

  // top bar
  editGlobalSettings: true,
  notifications: true,
  changeStatus: true,

  // Redacted File tab
  rightClickSaveRedactedMedia: false,
  downloadButtons: true,
  sendRedactedFile: true,
  deleteRedactedFile: true,

  // editor video tab
  toggleRedactions: true,
  selectClustersForMerge: true,
  addDetections: true,
  addBulkEffect: true,
  addFillEffects: true,
  clusterListFilter: true,
  removeFromOutput: true,
  editTdoName: true,
  editDetectionOrUDRsName: true,
  openClusterMenu: true,
  openOverlayMenu: true,
  dragOverlayObject: true,
  resizingOverlayObject: true,
  openSegmentMenu: true,
  customFill: true,
  showUndoRedo: true,
  editTimeLine: true,
  historySettings: true,
  rotateVideo: true,
  addUDRs: true,
  redactFile: true,
  editTrimRange: true,
  saveEditFiles: true,
  clearStatusHistory: true,
  editMergedDocument: true,
  clickOnBoundingBox: true,
  pinCluster: true,
  timelineResizeObjectType: true,

  // editor video - Audio Tab
  audioMenuDetails: true,

  // editor video - Comment Tab
  addNewComment: true,
  viewHiddenComments: true,
  viewFilter: true,
  sortComment: true,
  export: true,
  commentMenuDetails: true,

  // Keyboard shortcuts
  keyboardEditShortcuts: true,

  // Image mode specific permissions
  showAudioTab: true,
  showStickToVideo: true,
  showSetTimeStamp: true,
  showTrackOption: true,
  showTrimOptions: true,
  showDownloadRedactionReport: true,
};

export const viewOnlyPermissions: RolePermissions = {
  roleName: 'viewOnly',
  showBanner: true,
  lockTdo: false,
  isStatusLockEdits: false,
  // Set only by selectRolePermissionHelper's OLP branch, never by the base sets.
  isOlpViewOnly: false,

  createRequest: false,
  addFolder: false,
  deleteMedia: false,
  moveMedia: false,
  openMenuDetails: false,

  uploadMedia: false,
  unLinkDeleteActions: false,

  // top bar
  editGlobalSettings: false,
  notifications: false,

  // Redacted File tab
  rightClickSaveRedactedMedia: false,
  // Can hide if view only do not have permission
  downloadButtons: true,
  sendRedactedFile: false,
  deleteRedactedFile: false,

  // editor video tab
  detectObjects: false,
  showDetectObjects: true,
  generateVoiceMask: false,
  transcribeAudio: false,
  editDetections: false,
  toggleRedactions: false,
  addDetections: false,
  addBulkEffect: false,
  addFillEffects: false,
  clusterListFilter: true, // this could arguably be true - but should disable the remove from output
  removeFromOutput: false,
  editTdoName: false,
  editDetectionOrUDRsName: false,
  openClusterMenu: false,
  openOverlayMenu: false,
  dragOverlayObject: false,
  resizingOverlayObject: false,
  changeStatus: false,
  openSegmentMenu: false,
  customFill: false,
  showUndoRedo: false,
  editTimeLine: false,
  historySettings: false,
  rotateVideo: false,
  addUDRs: false,
  redactFile: false,
  editTrimRange: false,
  saveEditFiles: false,
  clearStatusHistory: false,
  // Overwritten by `selectRolePermissions` on every band, including this
  // one — see the interface. `false` here only so the shape is total.
  editMergedDocument: false,
  clickOnBoundingBox: false,
  pinCluster: false,
  timelineResizeObjectType: false,

  // editor video - Audio Tab
  audioMenuDetails: false,

  // editor video - Comment Tab
  addNewComment: true,
  viewHiddenComments: true,
  viewFilter: true,
  sortComment: true,
  export: true,
  commentMenuDetails: true,

  selectClustersForMerge: false,

  // Keyboard shortcuts
  keyboardEditShortcuts: false,

  // Image mode specific permissions
  showAudioTab: true,
  showStickToVideo: true,
  showSetTimeStamp: true,
  showTrackOption: true,
  showTrimOptions: true,
  showDownloadRedactionReport: true,
};

// reviewer gain ability to change status
export const reviewModePermissions: RolePermissions = {
  ...viewOnlyPermissions,
  roleName: 'review',
  changeStatus: true,
  sendRedactedFile: true, // perhaps we should prevent until complete?
  redactFile: true, // reviewers can (re)generate the redacted file even though they can't edit redactions
  lockTdo: true, // still lock the tdo to prevent other users from editing - distinction from view only
  // Explicit, and inherited by statusLockChangePermissions below: a status edit
  // lock must not be what takes this away. Only view-only denies it.
  clearStatusHistory: true,
  // deleteRedactedFile intentionally inherits false here: only editors may
  // delete the redacted output — the Manage section's Delete button stays
  // visible but disabled for reviewers.
};

export const statusLockChangePermissions: RolePermissions = {
  ...reviewModePermissions,
  showBanner: true,
  isStatusLockEdits: true,
  // A status-locked TDO — Pending Review or Complete, the two built-in
  // lock-editing statuses — may still be (re)redacted: a reviewer can
  // regenerate the output an editor forgot to burn in. Product confirmed this
  // holds for every lock-editing status, so `canTriggerRedactFile` deliberately
  // does NOT exclude the status lock (VE-26735).
  redactFile: true,
  // editTrimRange intentionally inherits false here: unlike redactFile, the
  // trim range cannot be edited while edits are status-locked.
  // Explicit even though review mode already inherits false: a status-locked
  // TDO must never allow deleting the redacted output, regardless of what
  // review mode grants in the future.
  deleteRedactedFile: false,
};

/**
 * May the user trigger Redact File right now? (VE-26735)
 *
 * The single lever every Redact File trigger gates on — the stale-banner
 * actions, the main buttons in the video/image and document editors, and the
 * document editor's export funnel (`handleExportRequest`, which Alt+R also goes
 * through) — instead of each site re-reading the permission.
 *
 * Currently equivalent to the bare `redactFile` permission: product confirmed a
 * status-locked TDO (Pending Review or Complete) may still be regenerated, so
 * the status lock is deliberately NOT excluded here. The only case it denies is
 * another user's lock / view-only, where `redactFile` is already `false`.
 * Keeping it as one named predicate means any future restriction (e.g. blocking
 * only Complete) lands in a single place.
 *
 * UI/UX gate only: nothing here stops a hand-dispatched REDACT_TDO/
 * EXPORT_REQUEST, so treat server-side enforcement as a separate concern.
 */
export const canTriggerRedactFile = (permissions: RolePermissions): boolean =>
  permissions.redactFile;
